import * as React from 'react';

export interface PulseState {
  value: string | undefined;
  pulse: number;
}

/** Only a present → present change counts as a move; a value appearing or
 *  disappearing resets the count so a remounted overlay never replays. */
export function nextPulse(state: PulseState, value: string | undefined): PulseState {
  const moved = state.value !== undefined && value !== undefined;
  return { value, pulse: moved ? state.pulse + 1 : 0 };
}

/** A count that rises each time `value` moves after mount (0 = never moved). */
export function useChangePulse(value: string | undefined): number {
  const [state, setState] = React.useState<PulseState>({ value, pulse: 0 });
  if (state.value !== value) {
    const next = nextPulse(state, value);
    setState(next);
    return next.pulse;
  }
  return state.pulse;
}

/**
 * A soft highlight behind one side of a line cell when its line or odds move.
 * Re-keying on each move restarts the CSS animation; the overlay settles at
 * opacity 0 when it ends. Its parent needs `relative isolate`.
 */
export const ChangePulse: React.FC<{ value: string | undefined }> = ({ value }) => {
  const pulse = useChangePulse(value);
  if (pulse === 0) return null;
  return (
    <span
      key={pulse}
      data-pulse
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 opacity-0 bg-brand-500 motion-safe:animate-line-pulse motion-reduce:bg-transparent motion-reduce:ring-1 motion-reduce:ring-inset motion-reduce:ring-brand-500 motion-reduce:animate-line-pulse-static"
    />
  );
};

export const linePulseValue = (line: { line: number; odds: number } | undefined): string | undefined =>
  line ? `${line.line}|${line.odds}` : undefined;
